// シナリオ一覧・編集のロジックを担うフック。
// fork_webapp_react の usePlaybacks（接続ライフサイクル）と useScenarioEdit
// （シナリオ CRUD / エディタ状態）を、plain React 向けに統合・移植したもの。
//
// - 機体へ接続し getMap からルート pool を算出、getCanvasParams / getBgImage を取得。
// - load_recorded_scenario を発行し、recorded_scenario トピックでシナリオ配列を受信。
// - シナリオの作成 / 更新 / 削除は save_recorded_scenario で機体側へ保存する。

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import mqttService, { type ConnectState } from '@/services/mqttService'
import type {
  EditableMark,
  MapCanvasParams,
  PlaybackRoute,
  PlaybackScenario,
  RouteMap,
  RouteWithAction,
  ActionType,
  ViewMode,
} from '@/types'
import { DIST_THRESHOLD } from '@/types'
import { extractRoutes } from '@/common/mapUtil'
import { applyRouteEditsToMap } from '@/common/mapEditSession'
import {
  extractEditableMarks,
  rotateEndpointTo,
  validateMarks,
} from '@/common/markUtil'
import type { SlotMoveJob } from '@/common/slotMoveUtil'
import {
  useRouteEdit,
  reRecordRoute,
  rewriteScenarios,
} from '@/hooks/useRouteEdit'
import {
  calcL1Distance,
  convertToRouteWithAction,
  createWaitResumeStep,
  createWaitStep,
  getSlotPosition,
  isTimedWaitStep,
  isWaitStep,
  lastPositionedEndSlot,
  lastRouteStep,
  normalizeWaitSec,
} from '@/common/routeUtil'
import {
  generateThumbnail,
  setCachedBgImage,
  clearCachedBgImage,
  THUMBNAIL_RENDER_SIZE,
} from '@/common/thumbnailUtil'
import {
  MAX_SCENARIOS,
  MAX_PLAYBACK_ROUTES,
  ENABLE_CARGO_TOGGLE,
  SLOT_TYPE_CARGO,
} from '@/config/scenarioConfig'

// 保存タイムアウト（ミリ秒）
const SAVE_TIMEOUT_MS = 10000
// 通知バナーの表示時間（ミリ秒）
const NOTICE_DURATION_MS = 3000

export type ScenarioErrorKind = 'connect' | 'load' | null

// マップ一括編集セッションの Undo/Redo 用スナップショット
interface MapEditSnapshot {
  routeMarks: Map<string, EditableMark[]>
  heights: Map<string, number>
}

function loadBgImage(data: ArrayBuffer): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const bytes = new Uint8Array(data)
    let mimeType = 'image/png'
    if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
      mimeType = 'image/jpeg'
    }
    const blob = new Blob([data], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const img = new Image()
    img.onload = () => {
      setCachedBgImage(img)
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load background image'))
    }
    img.src = url
  })
}

const DEFAULT_CANVAS_PARAMS: MapCanvasParams = {
  canvasWidthMeter: 20,
  imageOriginPixX: 64,
  imageOriginPixY: 130,
  imagePixelPerMeter: 6.2,
}

export function useScenarios(siteId: string | null, machineId: string | null) {
  // --- 接続・マップ関連の状態 ---
  const [map, setMap] = useState<RouteMap | null>(null)
  const [canvasParams, setCanvasParams] = useState<MapCanvasParams>(
    DEFAULT_CANVAS_PARAMS
  )
  const [routes, setRoutes] = useState<PlaybackRoute[]>([])
  const [scenarios, setScenarios] = useState<PlaybackScenario[]>([])
  const [bgImage, setBgImage] = useState<HTMLImageElement | null>(null)
  const [thumbnails, setThumbnails] = useState<Map<string, string>>(new Map())
  const [connectState, setConnectState] = useState<ConnectState>('disconnected')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<ScenarioErrorKind>(null)

  // --- エディタの状態 ---
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [cart, setCart] = useState<RouteWithAction[]>([])
  const [initialCart, setInitialCart] = useState<RouteWithAction[]>([])
  // コンポーザーで編集中のシナリオ名（任意）
  const [scenarioTitle, setScenarioTitle] = useState('')
  const [initialTitle, setInitialTitle] = useState('')
  const [editingScenarioId, setEditingScenarioId] = useState<string | null>(
    null
  )
  const [isSaving, setIsSaving] = useState(false)
  const [previewRoute, setPreviewRoute] = useState<PlaybackRoute | null>(null)
  const [previewAction, setPreviewAction] = useState<ActionType>('move')
  const [previewIsSwapped, setPreviewIsSwapped] = useState(false)
  // 停止手順の追加設定中か（ルート選択と排他）と、その停止秒数
  const [previewWait, setPreviewWait] = useState(false)
  const [previewWaitSec, setPreviewWaitSec] = useState(5)
  // 一時停止手順（タップで再開）の追加設定中か（ルート選択・停止手順と排他）
  const [previewWaitResume, setPreviewWaitResume] = useState(false)
  const [selectedRouteIndex, setSelectedRouteIndex] = useState<number | null>(
    null
  )
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteTargetIndex, setDeleteTargetIndex] = useState<number | null>(
    null
  )
  // 登録/更新（機体への保存）前の確認ダイアログ
  const [showSaveConfirm, setShowSaveConfirm] = useState(false)
  const [showEditorCancelConfirm, setShowEditorCancelConfirm] = useState(false)
  const [listDeleteTarget, setListDeleteTarget] = useState<{
    id: string
    index: number
  } | null>(null)
  const [isListDeleting, setIsListDeleting] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  // --- ブラウズ（マップ中心一覧）の選択状態（複数選択） ---
  const [selectedScenarioIds, setSelectedScenarioIds] = useState<Set<string>>(
    new Set()
  )
  const [selectedRouteIds, setSelectedRouteIds] = useState<Set<string>>(
    new Set()
  )

  // 保存完了待ちの解決関数
  const saveResolveRef = useRef<(() => void) | null>(null)
  // 最新の scenarios を保存処理から参照するための ref
  const scenariosRef = useRef<PlaybackScenario[]>([])
  useEffect(() => {
    scenariosRef.current = scenarios
  }, [scenarios])

  const noticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const showNotice = useCallback((message: string) => {
    setNotice(message)
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current)
    noticeTimerRef.current = setTimeout(
      () => setNotice(null),
      NOTICE_DURATION_MS
    )
  }, [])

  // --- 接続ライフサイクル ---
  useEffect(() => {
    if (!siteId || !machineId) {
      setMap(null)
      setRoutes([])
      setScenarios([])
      setBgImage(null)
      setThumbnails(new Map())
      setError(null)
      setSelectedScenarioIds(new Set())
      setSelectedRouteIds(new Set())
      return
    }

    let cancelled = false
    let startedConnecting = false
    let connected = false
    setMap(null)
    setRoutes([])
    setScenarios([])
    setBgImage(null)
    setThumbnails(new Map())
    setError(null)
    setViewMode('list')
    setSelectedScenarioIds(new Set())
    setSelectedRouteIds(new Set())
    setCart([])
    setEditingScenarioId(null)
    clearCachedBgImage()

    const unsubscribeBgImage = mqttService.onBgImageUpdate(async (data) => {
      if (cancelled) return
      try {
        const img = await loadBgImage(data)
        if (!cancelled) setBgImage(img)
      } catch {
        // 背景画像は任意
      }
    })

    // recorded_scenario 受信を監視（一覧更新 + 保存完了待ちの解決）
    const unsubscribeScenario = mqttService.onRecordedScenarioUpdate((data) => {
      if (cancelled || !data) return
      try {
        const parsed = JSON.parse(data)
        // 機体側が配列以外（空オブジェクト等）を返す場合は無視して既存一覧を維持
        // （[] で上書きするとシナリオ一覧が0件になってしまう）
        if (Array.isArray(parsed)) {
          setScenarios(parsed as PlaybackScenario[])
        } else {
          console.warn(
            'Recorded scenario data is not an array, keeping current list:',
            parsed
          )
        }
      } catch (e) {
        console.error('Failed to parse recorded scenario:', e)
      }
      if (saveResolveRef.current) {
        saveResolveRef.current()
        saveResolveRef.current = null
      }
    })

    const unsubscribeConnect = mqttService.onConnectionChange(async (state) => {
      if (cancelled) return
      setConnectState(state)

      if (state === 'connecting') {
        startedConnecting = true
        return
      }

      if (state === 'connected') {
        connected = true
        setIsLoading(true)
        try {
          const [loadedMap, params] = await Promise.all([
            mqttService.getMap(),
            mqttService.getCanvasParams(),
          ])
          if (cancelled) return
          setMap(loadedMap)
          setCanvasParams(params)
          setRoutes(extractRoutes(loadedMap))
          // 背景画像を要求（結果は onBgImageUpdate で受信）
          mqttService.getBgImage().catch(() => {})
          // 記録シナリオを要求（結果は onRecordedScenarioUpdate で受信）
          mqttService.loadRecordedScenario()
        } catch {
          if (cancelled) return
          setError('load')
        } finally {
          if (!cancelled) setIsLoading(false)
        }
        return
      }

      if (startedConnecting && !connected) {
        setError('connect')
      }
    })

    mqttService.connect(siteId, machineId)

    return () => {
      cancelled = true
      unsubscribeConnect()
      unsubscribeBgImage()
      unsubscribeScenario()
      mqttService.disconnect()
      clearCachedBgImage()
    }
  }, [siteId, machineId])

  // --- サムネイル生成（map / routes / scenarios / bgImage の変化で再生成） ---
  useEffect(() => {
    if (!map) return
    const cache = new Map<string, string>()

    // ルート単体のサムネイル（キー: rec-1 スロット ID）
    for (const route of routes) {
      const dataUrl = generateThumbnail({
        routes: [convertToRouteWithAction(route)],
        map,
        canvasParams,
        size: THUMBNAIL_RENDER_SIZE,
      })
      if (dataUrl) cache.set(route.slots[0], dataUrl)
    }

    // シナリオのサムネイル（キー: scenario.id）。停止手順は位置を持たないので除く
    for (const scenario of scenarios) {
      const scenarioRoutes = scenario.routes
        .filter((r) => !isWaitStep(r))
        .map((r) => ({
          ...r,
          action: (r.action || 'move') as ActionType,
          isSwapped: r.isSwapped || false,
          startSlot: r.startSlot || r.slots[0],
          endSlot: r.endSlot || r.slots[1] || r.slots[0],
        }))
      if (scenarioRoutes.length === 0) continue
      const dataUrl = generateThumbnail({
        routes: scenarioRoutes,
        map,
        canvasParams,
        size: THUMBNAIL_RENDER_SIZE,
      })
      if (dataUrl) cache.set(scenario.id, dataUrl)
    }

    setThumbnails(cache)
  }, [map, canvasParams, routes, scenarios, bgImage])

  // --- 機体側へシナリオを保存（応答を待つ） ---
  const saveToDevice = useCallback(
    async (newScenarios: PlaybackScenario[]): Promise<boolean> => {
      setIsSaving(true)
      mqttService.saveRecordedScenario(JSON.stringify(newScenarios))
      await new Promise((r) => setTimeout(r, 500))
      mqttService.loadRecordedScenario()

      return new Promise<boolean>((resolve) => {
        saveResolveRef.current = () => resolve(true)
        setTimeout(() => {
          if (saveResolveRef.current) {
            saveResolveRef.current = null
            resolve(false)
          }
        }, SAVE_TIMEOUT_MS)
      }).finally(() => {
        setIsSaving(false)
      })
    },
    []
  )

  // --- マップ・ルート一覧の再取得（ルート編集の保存後などに使用） ---
  const reloadMapData = useCallback(async () => {
    const loadedMap = await mqttService.getMap()
    setMap(loadedMap)
    setRoutes(extractRoutes(loadedMap))
  }, [])

  // --- スロット高さの一括変更 ---
  // set_recorded_slot_height は slots.csv の height 列のみを書き換えるため、
  // ルートの再記録が不要でルート ID（mapId）も変わらない。
  const [isSavingSlotHeights, setIsSavingSlotHeights] = useState(false)
  const applySlotHeightEdits = useCallback(
    async (edits: [string, number][]): Promise<boolean> => {
      setIsSavingSlotHeights(true)
      try {
        let count = 0
        for (const [id, heightMm] of edits) {
          // スロット ID（*r<mapId>/rec-N）からフォルダ名とスロット名を切り出す
          const m = id.match(/^\*r([^/]+)\/(.+)$/)
          if (!m) continue
          mqttService.setRecordedSlotHeight(m[1], m[2], heightMm / 1000)
          count++
        }
        if (count === 0) return true
        // 機体側が slots.csv を書き換えてマップを再読込するのを待ってから取得し直す
        await new Promise((r) => setTimeout(r, 500))
        await reloadMapData()
        showNotice(`${count}件のスロットの高さを変更しました`)
        return true
      } catch {
        showNotice('スロットの高さの変更に失敗しました')
        return false
      } finally {
        setIsSavingSlotHeights(false)
      }
    },
    [reloadMapData, showNotice]
  )

  // --- スロットの一括平行移動 ---
  // スロット位置には高さのような部分更新コマンドが無いため、対象ルートごとに
  // 移動後のマーク列で再記録する（ルート ID が変わる）。プランは
  // common/slotMoveUtil の planSlotMove で作り、ここでは逐次保存だけを担う。
  const [slotMoveProgress, setSlotMoveProgress] = useState<{
    done: number
    total: number
  } | null>(null)
  const isMovingSlotsRef = useRef(false)
  const applySlotMoveBulk = useCallback(
    async (jobs: SlotMoveJob[]): Promise<boolean> => {
      if (jobs.length === 0 || isMovingSlotsRef.current) return false
      isMovingSlotsRef.current = true
      setSlotMoveProgress({ done: 0, total: jobs.length })
      try {
        const knownIds = new Set(routes.map((r) => r.mapId))
        const currentMapId = await mqttService.getMapId()
        let currentScenarios = scenariosRef.current ?? []
        let scenariosChanged = false
        let done = 0
        let failed = false
        // 記録器（trajectory_command）は機体側で単一のため 1 ルートずつ順に再記録する
        for (const job of jobs) {
          const newRouteId = await reRecordRoute(
            job.marks,
            knownIds,
            currentMapId
          )
          if (!newRouteId) {
            failed = true
            break
          }
          knownIds.add(newRouteId)
          // 旧ルートを削除し、参照しているシナリオを新ルート ID へ書き換える
          mqttService.removeRecordedMap(job.oldMapId)
          const res = rewriteScenarios(
            currentScenarios,
            job.oldMapId,
            newRouteId
          )
          currentScenarios = res.scenarios
          scenariosChanged = scenariosChanged || res.changed
          await new Promise((r) => setTimeout(r, 500))
          done++
          setSlotMoveProgress({ done, total: jobs.length })
        }
        // 途中失敗でも、保存済みルートのシナリオ参照は必ず更新する
        if (scenariosChanged) {
          const ok = await saveToDevice(currentScenarios)
          if (!ok) {
            showNotice(
              'シナリオの参照更新に失敗しました。シナリオを確認してください'
            )
          }
        }
        await reloadMapData()
        if (failed) {
          showNotice(
            `ルートの再記録に失敗しました（${done}/${jobs.length} 件まで保存）`
          )
          return false
        }
        showNotice(`${jobs.length}件のルートを移動しました`)
        return true
      } finally {
        isMovingSlotsRef.current = false
        setSlotMoveProgress(null)
      }
    },
    [routes, scenariosRef, saveToDevice, showNotice, reloadMapData]
  )

  // --- マップ一括編集セッション（参照モード） ---
  // 移動・回転はルートごとの「編集後マーク列」、高さはスロット ID ごとの値として
  // ローカルに蓄積し、「まとめて保存」で高さの部分更新 + 対象ルートの再記録を
  // 一度に行う。編集後マップ（editedMap）を表示・次の編集の基準に使うことで、
  // 選択し直しながら複数回の編集を重ねられる。
  const [pendingRouteMarks, setPendingRouteMarks] = useState<
    Map<string, EditableMark[]>
  >(new Map())
  const [pendingHeights, setPendingHeights] = useState<Map<string, number>>(
    new Map()
  )
  const editedMap = useMemo(
    () => (map ? applyRouteEditsToMap(map, pendingRouteMarks) : null),
    [map, pendingRouteMarks]
  )
  const hasPendingMapEdits =
    pendingRouteMarks.size > 0 || pendingHeights.size > 0
  const pendingEditCounts = useMemo(
    () => ({ routes: pendingRouteMarks.size, heights: pendingHeights.size }),
    [pendingRouteMarks, pendingHeights]
  )

  // --- 編集セッションの Undo/Redo（スナップショット履歴） ---
  // stage 1回 = 履歴1操作。回転ハンドルのドラッグ中は begin/endMapEditBatch で
  // 囲み、連続する stage を1操作にまとめる。
  const [editPast, setEditPast] = useState<MapEditSnapshot[]>([])
  const [editFuture, setEditFuture] = useState<MapEditSnapshot[]>([])
  // 現在値のスナップショット（連続イベント中も stage 前の値を参照できるよう ref 保持）
  const sessionRef = useRef<MapEditSnapshot>({
    routeMarks: new Map(),
    heights: new Map(),
  })
  useEffect(() => {
    sessionRef.current = {
      routeMarks: pendingRouteMarks,
      heights: pendingHeights,
    }
  }, [pendingRouteMarks, pendingHeights])
  const batchBaseRef = useRef<MapEditSnapshot | null>(null)
  const batchChangedRef = useRef(false)

  // stage 前に呼ぶ。バッチ中は変更フラグのみ立て、バッチ終了時にまとめて積む
  const recordHistory = useCallback(() => {
    if (batchBaseRef.current) {
      batchChangedRef.current = true
    } else {
      setEditPast((p) => [...p, sessionRef.current])
      setEditFuture([])
    }
  }, [])

  const beginMapEditBatch = useCallback(() => {
    batchBaseRef.current = sessionRef.current
    batchChangedRef.current = false
  }, [])

  const endMapEditBatch = useCallback(() => {
    const base = batchBaseRef.current
    batchBaseRef.current = null
    if (base && batchChangedRef.current) {
      setEditPast((p) => [...p, base])
      setEditFuture([])
    }
    batchChangedRef.current = false
  }, [])

  const canUndoMapEdit = editPast.length > 0
  const canRedoMapEdit = editFuture.length > 0

  const undoMapEdit = useCallback(() => {
    if (editPast.length === 0) return
    const prev = editPast[editPast.length - 1]
    setEditPast(editPast.slice(0, -1))
    setEditFuture([
      ...editFuture,
      { routeMarks: pendingRouteMarks, heights: pendingHeights },
    ])
    setPendingRouteMarks(prev.routeMarks)
    setPendingHeights(prev.heights)
  }, [editPast, editFuture, pendingRouteMarks, pendingHeights])

  const redoMapEdit = useCallback(() => {
    if (editFuture.length === 0) return
    const next = editFuture[editFuture.length - 1]
    setEditFuture(editFuture.slice(0, -1))
    setEditPast([
      ...editPast,
      { routeMarks: pendingRouteMarks, heights: pendingHeights },
    ])
    setPendingRouteMarks(next.routeMarks)
    setPendingHeights(next.heights)
  }, [editPast, editFuture, pendingRouteMarks, pendingHeights])

  // 移動プラン（編集後マーク列）をセッションへ積む
  const stageRouteMarks = useCallback(
    (jobs: SlotMoveJob[]) => {
      recordHistory()
      setPendingRouteMarks((prev) => {
        const next = new Map(prev)
        for (const j of jobs) next.set(j.oldMapId, j.marks)
        return next
      })
    },
    [recordHistory]
  )

  // 選択スロットの高さ変更をセッションへ積む
  const stageSlotHeights = useCallback(
    (slotIds: string[], heightMm: number) => {
      recordHistory()
      setPendingHeights((prev) => {
        const next = new Map(prev)
        for (const id of slotIds) next.set(id, heightMm)
        return next
      })
    },
    [recordHistory]
  )

  // 端点スロットの向き変更をセッションへ積む（隣のマークを端点中心に回転）
  const stageEndpointRotation = useCallback(
    (slotId: string, theta: number): boolean => {
      if (!editedMap) return false
      const m = slotId.match(/^\*r([^/]+)\/(rec-[12])$/)
      if (!m) return false
      const marks =
        pendingRouteMarks.get(m[1]) ?? extractEditableMarks(editedMap, m[1])
      if (!marks) {
        showNotice('ルートのマークを読み取れませんでした')
        return false
      }
      const idx = m[2] === 'rec-1' ? 0 : marks.length - 1
      const next = rotateEndpointTo(marks, idx, theta)
      if (!next || validateMarks(next).length > 0) {
        showNotice('この向きには回転できません（マーク間隔を確認してください）')
        return false
      }
      recordHistory()
      setPendingRouteMarks((prev) => new Map(prev).set(m[1], next))
      return true
    },
    [editedMap, pendingRouteMarks, showNotice, recordHistory]
  )

  const discardPendingMapEdits = useCallback(() => {
    setPendingRouteMarks(new Map())
    setPendingHeights(new Map())
    setEditPast([])
    setEditFuture([])
    batchBaseRef.current = null
    batchChangedRef.current = false
  }, [])

  // 機体切替などでマップが失われたらセッションを破棄する
  useEffect(() => {
    if (!map) discardPendingMapEdits()
  }, [map, discardPendingMapEdits])

  // セッションの未保存編集をまとめて機体へ保存する。
  // 再記録対象ルートの端点高さはマーク列に織り込んで1回の再記録で済ませ、
  // それ以外の高さは set_recorded_slot_height（部分更新）で変更する。
  const applyPendingMapEdits = useCallback(async (): Promise<boolean> => {
    const routeEdits = new Map(pendingRouteMarks)
    const heightOnly: [string, number][] = []
    for (const [slotId, heightMm] of pendingHeights) {
      const m = slotId.match(/^\*r([^/]+)\/(rec-[12])$/)
      const marks = m ? routeEdits.get(m[1]) : undefined
      if (m && marks) {
        const idx = m[2] === 'rec-1' ? 0 : marks.length - 1
        if (marks[idx]?.isCargo) {
          routeEdits.set(
            m[1],
            marks.map((mk, i) =>
              i === idx ? { ...mk, height: heightMm / 1000 } : mk
            )
          )
          continue
        }
      }
      heightOnly.push([slotId, heightMm])
    }
    let ok = true
    if (heightOnly.length > 0) {
      ok = await applySlotHeightEdits(heightOnly)
    }
    if (ok && routeEdits.size > 0) {
      ok = await applySlotMoveBulk(
        [...routeEdits].map(([oldMapId, marks]) => ({ oldMapId, marks }))
      )
    }
    if (ok) discardPendingMapEdits()
    return ok
  }, [
    pendingRouteMarks,
    pendingHeights,
    applySlotHeightEdits,
    applySlotMoveBulk,
    discardPendingMapEdits,
  ])

  // --- ルート編集（マーク列の編集） ---
  const routeEdit = useRouteEdit({
    map,
    routes,
    scenarios,
    scenariosRef,
    saveToDevice,
    showNotice,
    reloadMapData,
    setViewMode,
  })

  // --- computed ---
  const isMaxScenariosReached = scenarios.length >= MAX_SCENARIOS

  // カートが初期状態から変更されたか（タイトル・手順の並び・停止秒数）
  const hasChanges = useMemo(() => {
    if (scenarioTitle.trim() !== initialTitle.trim()) return true
    if (cart.length !== initialCart.length) return true
    return cart.some(
      (route, index) =>
        route.id !== initialCart[index]?.id ||
        (route.waitSec ?? 0) !== (initialCart[index]?.waitSec ?? 0)
    )
  }, [cart, initialCart, scenarioTitle, initialTitle])

  // カート内ルート間のエラー（距離・荷物始点・荷物整合）
  const cartErrors = useMemo(() => {
    // 各ルート開始時点の荷物の有無を先頭から辿る。先頭ルートは制限を設けない
    // 方針に合わせ、初期状態は先頭ルートのアクションから推定する
    // （先頭の荷物あり移動・荷下ろしは「荷物を持った状態からの開始」とみなす）
    const wasCarryingList: boolean[] = []
    let carrying = false
    for (const route of cart) {
      wasCarryingList.push(carrying)
      const action = route.action ?? 'move'
      if (action === 'pickup' || action === 'move_loaded') {
        carrying = true
      } else if (action === 'place') {
        carrying = false
      }
    }
    return cart
      .map((route, idx) => {
        // 停止手順: 秒数未入力（0）はエラー。位置は直前手順の終点なので距離だけ確認する
        // （先頭の停止手順は位置を持たないため距離は見ない）
        if (isWaitStep(route)) {
          // 秒数未入力はエラー（一時停止手順は秒数を持たないので対象外）
          const waitError = isTimedWaitStep(route) && (route.waitSec ?? 0) < 1
          const prevEndSlot = lastPositionedEndSlot(cart.slice(0, idx))
          const distance =
            prevEndSlot && route.startSlot
              ? calcL1Distance(
                  getSlotPosition(map, prevEndSlot),
                  getSlotPosition(map, route.startSlot)
                )
              : 0
          const distanceError =
            distance > DIST_THRESHOLD ? distance.toFixed(3) : null
          if (!waitError && !distanceError) return null
          return {
            index: idx,
            distanceError,
            cargoError: false,
            sequenceError: null,
            cargoStateError: null,
            waitError,
          }
        }
        if (idx === 0) return null
        const action = route.action ?? 'move'
        const wasCarrying = wasCarryingList[idx]
        // 距離は直前の「位置を持つ」手順の終点と比較する
        // （先頭の停止手順は位置を持たないので、その直後のルートは自由に置ける）
        const prevEndSlot = lastPositionedEndSlot(cart.slice(0, idx))
        const distance = prevEndSlot
          ? calcL1Distance(
              getSlotPosition(map, prevEndSlot),
              getSlotPosition(map, route.startSlot!)
            )
          : 0
        // 荷役・並びの判定は停止手順を飛ばした直前のルートで行う
        const prevRoute = lastRouteStep(cart.slice(0, idx))

        const distanceError =
          distance > DIST_THRESHOLD ? distance.toFixed(3) : null
        const cargoError =
          ENABLE_CARGO_TOGGLE &&
          (prevRoute?.action === 'pickup' || prevRoute?.action === 'place') &&
          map?.slots.get(route.startSlot!)?.type !== SLOT_TYPE_CARGO
        // 荷積み/荷物あり移動→移動のみ / 移動のみ→荷物あり移動 の並びは不可
        // （途中のルート削除などで生じ得るためカート全体でも検証する）
        const prevAction = prevRoute?.action ?? 'move'
        const sequenceError: 'moveAfterLoaded' | 'loadedAfterMove' | null =
          (prevAction === 'move_loaded' || prevAction === 'pickup') &&
          action === 'move'
            ? 'moveAfterLoaded'
            : prevAction === 'move' && action === 'move_loaded'
              ? 'loadedAfterMove'
              : null
        // 荷物の有無と矛盾する荷積み・荷下ろし・荷物あり移動は配置不可
        // （荷積み→荷積み / 荷下ろし→荷下ろし など必ず荷物不一致になる並びを防ぐ）
        const cargoStateError:
          | 'pickupWhileLoaded'
          | 'placeWithoutLoad'
          | 'loadedAfterPlace'
          | null =
          action === 'pickup' && wasCarrying
            ? 'pickupWhileLoaded'
            : action === 'place' && !wasCarrying
              ? 'placeWithoutLoad'
              : action === 'move_loaded' &&
                  !wasCarrying &&
                  prevAction === 'place'
                ? 'loadedAfterPlace'
                : null

        if (!distanceError && !cargoError && !sequenceError && !cargoStateError)
          return null
        return {
          index: idx,
          distanceError,
          cargoError,
          sequenceError,
          cargoStateError,
          waitError: false,
        }
      })
      .filter((e): e is NonNullable<typeof e> => e !== null)
  }, [cart, map])

  const hasDistanceError = cartErrors.some((e) => e.distanceError !== null)
  const hasCargoStartError = cartErrors.some((e) => e.cargoError)
  const hasSequenceError = cartErrors.some((e) => e.sequenceError !== null)
  const hasCargoStateError = cartErrors.some((e) => e.cargoStateError !== null)
  const hasWaitError = cartErrors.some((e) => e.waitError)

  // 他シナリオで使用されているルートの mapId セット（停止手順は除く）
  const otherScenarioRouteMapIds = useMemo(() => {
    const ids = new Set<string>()
    for (const scenario of scenarios) {
      if (scenario.id === editingScenarioId) continue
      for (const route of scenario.routes) {
        if (!isWaitStep(route)) ids.add(route.mapId)
      }
    }
    return ids
  }, [scenarios, editingScenarioId])

  // ルートが地図から削除され利用できなくなったシナリオのIDセット
  // (地図読み込み前はルート一覧が空のため判定しない。停止手順は判定対象外)
  const unavailableScenarioIds = useMemo(() => {
    const ids = new Set<string>()
    if (!map) return ids
    const availableMapIds = new Set(routes.map((route) => route.mapId))
    for (const scenario of scenarios) {
      if (
        scenario.routes.some(
          (route) => !isWaitStep(route) && !availableMapIds.has(route.mapId)
        )
      ) {
        ids.add(scenario.id)
      }
    }
    return ids
  }, [map, routes, scenarios])

  // カートに追加済みルートの mapId → 使用位置(1始まり)（停止手順は除く）
  const currentScenarioRouteMapIds = useMemo(() => {
    const positions = new Map<string, number[]>()
    cart.forEach((route, index) => {
      if (isWaitStep(route)) return
      const existing = positions.get(route.mapId)
      if (existing) existing.push(index + 1)
      else positions.set(route.mapId, [index + 1])
    })
    return positions
  }, [cart])

  const previewStartSlot = previewRoute
    ? previewIsSwapped
      ? previewRoute.slots[1]
      : previewRoute.slots[0]
    : null
  const previewEndSlot = previewRoute
    ? previewIsSwapped
      ? previewRoute.slots[0]
      : previewRoute.slots[1]
    : null

  const isEndSlotCargo = previewEndSlot
    ? map?.slots.get(previewEndSlot)?.type === SLOT_TYPE_CARGO
    : false
  const isStartSlotCargo = previewStartSlot
    ? map?.slots.get(previewStartSlot)?.type === SLOT_TYPE_CARGO
    : false

  // カート末尾のルート（停止手順を除く）が荷積み/荷下ろしの場合、次は高さ保存マーク始点が必須
  const lastCartRoute = lastRouteStep(cart)
  const requiresCargoStart =
    lastCartRoute !== null &&
    (lastCartRoute.action === 'pickup' || lastCartRoute.action === 'place')
  const isCargoStartValid =
    !ENABLE_CARGO_TOGGLE || !requiresCargoStart || isStartSlotCargo

  // カート末尾時点で荷物を持っているか。
  // 末尾ルートが移動系でも、それ以前の荷積み・荷下ろしまで遡って判定する
  const cartCarrying = useMemo(() => {
    let carrying = false
    for (const route of cart) {
      const action = route.action ?? 'move'
      if (action === 'pickup' || action === 'move_loaded') {
        carrying = true
      } else if (action === 'place') {
        carrying = false
      }
    }
    return carrying
  }, [cart])

  // 荷物を持っている間は「移動のみ」「荷積み」不可、
  // 持っていない間は「荷物あり移動」「荷下ろし」不可
  // （荷積み→荷積み / 荷下ろし→荷下ろし など荷物不一致になる並びを防ぐ）。
  // 先頭ルートには制限を設けない（荷物を持った状態からの開始も許可する）。
  const isMoveDisabled = cartCarrying
  const isMoveLoadedDisabled = cart.length > 0 && !cartCarrying
  const isPickupDisabled = cartCarrying
  const isPlaceDisabled = cart.length > 0 && !cartCarrying

  // 「移動のみ」が選択不可の場合に使う既定アクション
  const defaultMoveAction: ActionType = isMoveDisabled ? 'move_loaded' : 'move'

  const distanceInfo = useMemo(() => {
    if (!previewRoute || !previewStartSlot) return null
    // カートが空、または先頭の停止手順しか無い（位置を持つ手順が無い）間は制限なし
    const lastEndSlot = lastPositionedEndSlot(cart)
    if (!lastEndSlot) return { distance: 0, isValid: true }
    const lastEndPos = getSlotPosition(map, lastEndSlot)
    const currentStartPos = getSlotPosition(map, previewStartSlot)
    const distance = calcL1Distance(lastEndPos, currentStartPos)
    return { distance, isValid: distance <= DIST_THRESHOLD }
  }, [previewRoute, previewStartSlot, cart, map])

  // カート末尾ルートの終点に順方向/逆方向いずれかで接続できるかをルートごとに判定。
  // requiresSwap は逆方向でのみ接続できる場合 true（選択時に方向を自動反転する）
  const routeConnectivity = useMemo(() => {
    const connectivity = new Map<
      string,
      { connectable: boolean; requiresSwap: boolean }
    >()
    // 位置を持つ末尾手順の終点（先頭の停止手順は位置を持たないので飛ばす）
    const lastEndSlot = lastPositionedEndSlot(cart)
    const lastEndPos = lastEndSlot ? getSlotPosition(map, lastEndSlot) : null
    routes.forEach((route) => {
      // カートに位置を持つ手順が無い、またはマップ未取得で座標比較できない間は
      // 全ルートを選択可能にする
      if (!lastEndPos) {
        connectivity.set(route.id, { connectable: true, requiresSwap: false })
        return
      }
      const forwardDistance = calcL1Distance(
        lastEndPos,
        getSlotPosition(map, route.slots[0])
      )
      const reverseDistance = calcL1Distance(
        lastEndPos,
        getSlotPosition(map, route.slots[1])
      )
      const forwardOk = forwardDistance <= DIST_THRESHOLD
      const reverseOk = reverseDistance <= DIST_THRESHOLD
      connectivity.set(route.id, {
        connectable: forwardOk || reverseOk,
        requiresSwap: !forwardOk && reverseOk,
      })
    })
    return connectivity
  }, [routes, cart, map])

  const isRouteConnectable = useCallback(
    (route: PlaybackRoute) =>
      routeConnectivity.get(route.id)?.connectable ?? true,
    [routeConnectivity]
  )

  // 接続可能なルートを先頭に並べた表示用のルート一覧
  // （接続可・不可の各グループ内は日付の新しい順）
  const sortedRoutes = useMemo(
    () =>
      [...routes].sort((a, b) => {
        const connectableDiff =
          Number(isRouteConnectable(b)) - Number(isRouteConnectable(a))
        if (connectableDiff !== 0) return connectableDiff
        return b.mapId.localeCompare(a.mapId)
      }),
    [routes, isRouteConnectable]
  )

  const isAddDisabled =
    (distanceInfo !== null && !distanceInfo.isValid) || !isCargoStartValid

  // 停止手順の停止位置 = 位置を持つ末尾手順の終点。カートが空（シナリオ先頭）なら
  // 位置なしの停止手順になり、出発位置で停止する
  const lastCartEndSlot = lastPositionedEndSlot(cart)
  const isAddWaitDisabled = previewWaitSec < 1

  const isCreateDisabled =
    cart.length === 0 ||
    !!previewRoute ||
    previewWait ||
    previewWaitResume ||
    hasDistanceError ||
    hasCargoStartError ||
    hasSequenceError ||
    hasCargoStateError ||
    hasWaitError ||
    isSaving

  // プレビュー用のルート配列（カート + プレビュー中ルート / 設定中の停止手順）
  const previewRoutes: RouteWithAction[] = previewRoute
    ? [
        ...cart,
        {
          ...previewRoute,
          id: `preview-${previewRoute.id}`,
          action: previewAction,
          isSwapped: previewIsSwapped,
          startSlot: previewStartSlot!,
          endSlot: previewEndSlot!,
        },
      ]
    : previewWait
      ? [
          ...cart,
          createWaitStep(lastCartEndSlot, previewWaitSec, 'preview-wait'),
        ]
      : previewWaitResume
        ? [
            ...cart,
            createWaitResumeStep(lastCartEndSlot, 'preview-wait-resume'),
          ]
        : cart

  // --- handlers: page ---
  const handleAddToCart = useCallback(
    (newRoute: RouteWithAction) => {
      const lastEndSlot = lastPositionedEndSlot(cart)
      if (lastEndSlot) {
        const lastEndPos = getSlotPosition(map, lastEndSlot)
        const currentStartPos = getSlotPosition(map, newRoute.startSlot!)
        const distance = calcL1Distance(lastEndPos, currentStartPos)
        if (distance > DIST_THRESHOLD) {
          showNotice(
            `前のポイントから遠すぎます (距離: ${distance.toFixed(3)}m)`
          )
          return
        }
      }
      setCart((prev) => [...prev, newRoute])
    },
    [cart, map, showNotice]
  )

  const handleRemoveFromCart = useCallback((index: number) => {
    setCart((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleCreateScenario = useCallback(async () => {
    if (cart.length === 0) {
      showNotice('ルートを1つ以上追加してください')
      return
    }

    const currentScenarios = scenariosRef.current
    let newScenarios: PlaybackScenario[]
    const isEdit = viewMode === 'edit' && editingScenarioId
    const title = scenarioTitle.trim() || undefined

    if (isEdit) {
      if (!currentScenarios.some((s) => s.id === editingScenarioId)) {
        showNotice('編集対象のシナリオが見つかりません。再読み込みしてください')
        return
      }
      newScenarios = currentScenarios.map((s) =>
        s.id === editingScenarioId ? { ...s, title, routes: cart } : s
      )
    } else {
      const newScenario: PlaybackScenario = {
        id: `scenario-${Date.now()}`,
        title,
        routes: cart,
        createdAt: new Date().toISOString(),
      }
      newScenarios = [...currentScenarios, newScenario]
    }

    const success = await saveToDevice(newScenarios)
    if (success) {
      showNotice(isEdit ? 'シナリオを更新しました' : 'シナリオを作成しました')
      setCart([])
      setScenarioTitle('')
      setInitialTitle('')
      setEditingScenarioId(null)
      setViewMode('list')
    } else {
      showNotice('シナリオの保存に失敗しました（タイムアウト）')
    }
  }, [
    cart,
    scenarioTitle,
    viewMode,
    editingScenarioId,
    saveToDevice,
    showNotice,
  ])

  // 登録/更新ボタン: 確認ダイアログを開く（機体への保存であることを明示）
  const handleRequestSave = useCallback(() => {
    if (cart.length === 0) {
      showNotice('ルートを1つ以上追加してください')
      return
    }
    setShowSaveConfirm(true)
  }, [cart, showNotice])

  const handleConfirmSave = useCallback(async () => {
    await handleCreateScenario()
    setShowSaveConfirm(false)
  }, [handleCreateScenario])

  const handleCancelSave = useCallback(() => {
    if (!isSaving) setShowSaveConfirm(false)
  }, [isSaving])

  const handleDeleteScenario = useCallback(
    async (scenarioId: string) => {
      const currentScenarios = scenariosRef.current
      const target = currentScenarios.find((s) => s.id === scenarioId)
      if (!target) {
        showNotice('削除対象のシナリオが見つかりません。再読み込みしてください')
        return
      }
      const filtered = currentScenarios.filter((s) => s.id !== scenarioId)
      const success = await saveToDevice(filtered)
      showNotice(
        success
          ? 'シナリオを削除しました'
          : 'シナリオの削除に失敗しました（タイムアウト）'
      )
    },
    [saveToDevice, showNotice]
  )

  const handleStartCreate = useCallback(() => {
    if (isMaxScenariosReached) {
      showNotice(`シナリオは最大${MAX_SCENARIOS}件まで作成できます`)
      return
    }
    setViewMode('create')
    setCart([])
    setInitialCart([])
    setScenarioTitle('')
    setInitialTitle('')
    setEditingScenarioId(null)
    setPreviewRoute(null)
  }, [isMaxScenariosReached, showNotice])

  const handleStartEdit = useCallback(
    (scenario: PlaybackScenario) => {
      setViewMode('edit')
      const routesWithAction = scenario.routes.map(convertToRouteWithAction)
      // 地図から削除されたルートはカートに展開せず除外する。
      // initialCart には元の内容を残して hasChanges を成立させ、「更新」で除外を保存できるようにする
      // (地図読み込み前はルート一覧が空のため除外しない)
      const availableMapIds = new Set(routes.map((route) => route.mapId))
      const availableCart = map
        ? routesWithAction.filter(
            (route) => isWaitStep(route) || availableMapIds.has(route.mapId)
          )
        : routesWithAction
      if (availableCart.length < routesWithAction.length) {
        showNotice(
          '削除されたルートをシナリオから除外しました。「更新」を押すと保存されます'
        )
      }
      setCart(availableCart)
      setInitialCart(routesWithAction)
      setScenarioTitle(scenario.title ?? '')
      setInitialTitle(scenario.title ?? '')
      setEditingScenarioId(scenario.id)
      setPreviewRoute(null)
    },
    [map, routes, showNotice]
  )

  const handleCancelCreate = useCallback(() => {
    setViewMode('list')
    setCart([])
    setInitialCart([])
    setScenarioTitle('')
    setInitialTitle('')
    setEditingScenarioId(null)
    setPreviewRoute(null)
  }, [])

  // --- handlers: editor ---
  const handleSelectRoute = useCallback(
    (route: PlaybackRoute) => {
      setPreviewWait(false)
      setPreviewWaitResume(false)
      if (previewRoute?.id === route.id) {
        setPreviewRoute(null)
      } else {
        if (!isRouteConnectable(route)) return
        setPreviewRoute(route)
        setPreviewAction(defaultMoveAction)
        // 逆方向でのみ接続できるルートは最初から反転した向きで選択する
        setPreviewIsSwapped(
          routeConnectivity.get(route.id)?.requiresSwap ?? false
        )
      }
    },
    [previewRoute, defaultMoveAction, isRouteConnectable, routeConnectivity]
  )

  // 停止手順の選択（トグル動作。ルート選択・一時停止手順とは排他）
  const handleSelectWait = useCallback(() => {
    setPreviewRoute(null)
    setPreviewWaitResume(false)
    setPreviewWait((v) => !v)
  }, [])

  // 一時停止手順（タップで再開）の選択（トグル動作。ルート選択・停止手順とは排他）
  const handleSelectWaitResume = useCallback(() => {
    setPreviewRoute(null)
    setPreviewWait(false)
    setPreviewWaitResume((v) => !v)
  }, [])

  // 一時停止手順をカートへ追加（停止位置の扱いは停止手順と同じ）
  const handleConfirmAddWaitResume = useCallback(() => {
    setCart((prev) => [...prev, createWaitResumeStep(lastCartEndSlot)])
    setPreviewWaitResume(false)
  }, [lastCartEndSlot])

  // 停止手順の秒数変更（設定中）
  const handlePreviewWaitSecChange = useCallback((sec: number) => {
    setPreviewWaitSec(normalizeWaitSec(sec))
  }, [])

  // 停止手順をカートへ追加（停止位置は位置を持つ末尾手順の終点スロット。無ければ位置なし）
  const handleConfirmAddWait = useCallback(() => {
    if (previewWaitSec < 1) return
    setCart((prev) => [
      ...prev,
      createWaitStep(lastCartEndSlot, previewWaitSec),
    ])
    setPreviewWait(false)
  }, [lastCartEndSlot, previewWaitSec])

  // カート内の停止手順の秒数変更（追加後に使用ルート一覧で直接変更できるようにする）
  const handleCartWaitSecChange = useCallback((index: number, sec: number) => {
    setCart((prev) =>
      prev.map((route, i) =>
        i === index && isTimedWaitStep(route)
          ? { ...route, waitSec: normalizeWaitSec(sec) }
          : route
      )
    )
  }, [])

  const handleConfirmAdd = useCallback(() => {
    if (!previewRoute || !previewStartSlot || !previewEndSlot) return
    const newRoute: RouteWithAction = {
      ...previewRoute,
      id: `${previewRoute.id}-${Date.now()}`,
      action: previewAction,
      isSwapped: previewIsSwapped,
      startSlot: previewStartSlot,
      endSlot: previewEndSlot,
    }
    handleAddToCart(newRoute)
    setPreviewRoute(null)
  }, [
    previewRoute,
    previewStartSlot,
    previewEndSlot,
    previewAction,
    previewIsSwapped,
    handleAddToCart,
  ])

  const handleSwapDirection = useCallback(() => {
    const newIsSwapped = !previewIsSwapped
    setPreviewIsSwapped(newIsSwapped)
    const newEndSlot = previewRoute
      ? newIsSwapped
        ? previewRoute.slots[0]
        : previewRoute.slots[1]
      : null
    const newEndSlotIsCargo = newEndSlot
      ? map?.slots.get(newEndSlot)?.type === SLOT_TYPE_CARGO
      : false
    // 荷積み・荷下ろしは高さ保存マーク終点が必須。終点が非対象になった場合のみ
    // 既定の移動アクションへ戻す（荷物あり移動は終点種別を問わないので維持する）。
    if (
      !newEndSlotIsCargo &&
      (previewAction === 'pickup' || previewAction === 'place')
    ) {
      setPreviewAction(defaultMoveAction)
    }
  }, [previewIsSwapped, previewRoute, map, previewAction, defaultMoveAction])

  const handleRequestDelete = useCallback((index: number) => {
    setDeleteTargetIndex(index)
    setShowDeleteConfirm(true)
  }, [])

  const handleCancelDelete = useCallback(() => {
    setDeleteTargetIndex(null)
    setShowDeleteConfirm(false)
  }, [])

  const handleConfirmRouteDelete = useCallback(() => {
    if (deleteTargetIndex !== null) handleRemoveFromCart(deleteTargetIndex)
    handleCancelDelete()
  }, [deleteTargetIndex, handleRemoveFromCart, handleCancelDelete])

  const handleEditorCancel = useCallback(() => {
    if (previewRoute || previewWait || previewWaitResume) {
      setPreviewRoute(null)
      setPreviewWait(false)
      setPreviewWaitResume(false)
    } else if (hasChanges) {
      setShowEditorCancelConfirm(true)
    } else {
      handleCancelCreate()
    }
  }, [
    previewRoute,
    previewWait,
    previewWaitResume,
    hasChanges,
    handleCancelCreate,
  ])

  const handleConfirmEditorCancel = useCallback(() => {
    setShowEditorCancelConfirm(false)
    handleCancelCreate()
  }, [handleCancelCreate])

  const handleDismissEditorCancel = useCallback(() => {
    setShowEditorCancelConfirm(false)
  }, [])

  const handleToggleSelectedRoute = useCallback(
    (index: number) => {
      setSelectedRouteIndex(selectedRouteIndex === index ? null : index)
    },
    [selectedRouteIndex]
  )

  const handlePreviewActionChange = useCallback((action: ActionType) => {
    setPreviewAction(action)
  }, [])

  // --- handlers: list ---
  const handleRequestListDelete = useCallback((id: string, index: number) => {
    setListDeleteTarget({ id, index })
  }, [])

  const handleConfirmListDelete = useCallback(async () => {
    if (listDeleteTarget) {
      setIsListDeleting(true)
      await handleDeleteScenario(listDeleteTarget.id)
      setIsListDeleting(false)
      setListDeleteTarget(null)
    }
  }, [listDeleteTarget, handleDeleteScenario])

  const handleCancelListDelete = useCallback(() => {
    if (!isListDeleting) setListDeleteTarget(null)
  }, [isListDeleting])

  // --- ブラウズ（マップ中心一覧）の computed / handlers ---

  // マップに表示するルート群。ズームを変えないよう常に全ルートを表示し、
  // 選択対象はハイライトのみで強調する（表示セットは差し替えない）。
  const displayRoutes: RouteWithAction[] = useMemo(
    () => routes.map(convertToRouteWithAction),
    [routes]
  )

  // 選択中シナリオの構成ルート（使用ルート一覧パネル用）。複数選択時は順に連結。
  const selectedScenarioRoutes = useMemo(() => {
    const out: RouteWithAction[] = []
    for (const sc of scenarios) {
      if (!selectedScenarioIds.has(sc.id)) continue
      for (const r of sc.routes) out.push(convertToRouteWithAction(r))
    }
    return out
  }, [scenarios, selectedScenarioIds])

  // 選択中シナリオの構成ルートの mapId 集合
  const selectedScenarioMapIds = useMemo(() => {
    const ids = new Set<string>()
    for (const sc of scenarios) {
      if (!selectedScenarioIds.has(sc.id)) continue
      for (const r of sc.routes) ids.add(r.mapId)
    }
    return ids
  }, [scenarios, selectedScenarioIds])

  // ハイライトする displayRoutes 内インデックスの集合。
  // 選択ルート（id 一致）と、選択シナリオの構成ルート（mapId 一致）の和集合。
  const highlightedBrowseIndices = useMemo(() => {
    const set = new Set<number>()
    displayRoutes.forEach((r, i) => {
      if (selectedRouteIds.has(r.id) || selectedScenarioMapIds.has(r.mapId)) {
        set.add(i)
      }
    })
    return set
  }, [displayRoutes, selectedRouteIds, selectedScenarioMapIds])

  // Explorer 風の複数選択（クリック=単一 / Ctrl・Cmd=トグル / Shift=範囲）。
  // 直前クリック位置（アンカー）は Shift 範囲選択の起点に使う。
  const scenarioAnchorRef = useRef<number | null>(null)
  const routeAnchorRef = useRef<number | null>(null)

  const selectScenarioAt = useCallback(
    (index: number, mods: { ctrl: boolean; shift: boolean }) => {
      const orderedIds = scenarios.map((sc) => sc.id)
      const id = orderedIds[index]
      const anchor = scenarioAnchorRef.current
      setSelectedScenarioIds((prev) => {
        if (mods.shift && anchor != null) {
          const a = Math.min(anchor, index)
          const b = Math.max(anchor, index)
          const range = orderedIds.slice(a, b + 1)
          return mods.ctrl ? new Set([...prev, ...range]) : new Set(range)
        }
        if (mods.ctrl) {
          const next = new Set(prev)
          if (next.has(id)) next.delete(id)
          else next.add(id)
          return next
        }
        return new Set([id])
      })
      // シナリオ選択時はルート選択を解除（排他）
      setSelectedRouteIds(new Set())
      if (!(mods.shift && anchor != null)) scenarioAnchorRef.current = index
    },
    [scenarios]
  )

  const selectRouteAt = useCallback(
    (index: number, mods: { ctrl: boolean; shift: boolean }) => {
      const orderedIds = routes.map((r) => r.id)
      const id = orderedIds[index]
      const anchor = routeAnchorRef.current
      setSelectedRouteIds((prev) => {
        if (mods.shift && anchor != null) {
          const a = Math.min(anchor, index)
          const b = Math.max(anchor, index)
          const range = orderedIds.slice(a, b + 1)
          return mods.ctrl ? new Set([...prev, ...range]) : new Set(range)
        }
        if (mods.ctrl) {
          const next = new Set(prev)
          if (next.has(id)) next.delete(id)
          else next.add(id)
          return next
        }
        return new Set([id])
      })
      // ルート選択時はシナリオ選択を解除（排他）
      setSelectedScenarioIds(new Set())
      if (!(mods.shift && anchor != null)) routeAnchorRef.current = index
    },
    [routes]
  )

  // マップクリック用の修飾キー対応選択（クリック=単一 / Ctrl・Cmd=トグル / Shift=追加）。
  const selectRouteWithMods = useCallback(
    (id: string, mods: { ctrl: boolean; shift: boolean }) => {
      setSelectedRouteIds((prev) => {
        if (mods.shift && !mods.ctrl) {
          const next = new Set(prev)
          next.add(id)
          return next
        }
        if (mods.ctrl) {
          const next = new Set(prev)
          if (next.has(id)) next.delete(id)
          else next.add(id)
          return next
        }
        return new Set([id])
      })
      // ルート選択時はシナリオ選択を解除（排他）
      setSelectedScenarioIds(new Set())
      const idx = routes.findIndex((r) => r.id === id)
      if (idx >= 0) routeAnchorRef.current = idx
    },
    [routes]
  )

  const handleClearBrowseSelection = useCallback(() => {
    setSelectedScenarioIds(new Set())
    setSelectedRouteIds(new Set())
  }, [])

  return {
    // connection / map
    map,
    canvasParams,
    bgImage,
    imageLoaded: bgImage !== null,
    routes,
    scenarios,
    thumbnails,
    connectState,
    isLoading,
    error,
    notice,

    // route edit (マーク列の編集)
    routeEdit,

    // editor state
    viewMode,
    cart,
    scenarioTitle,
    setScenarioTitle,
    isSaving,
    previewRoute,
    previewAction,
    previewWait,
    previewWaitSec,
    previewWaitResume,
    selectedRouteIndex,
    showDeleteConfirm,
    showSaveConfirm,
    deleteTargetIndex,
    showEditorCancelConfirm,
    listDeleteTarget,
    isListDeleting,

    // browse (map-centric list)
    selectedScenarioIds,
    selectedRouteIds,
    selectedScenarioRoutes,
    displayRoutes,
    highlightedBrowseIndices,
    selectScenarioAt,
    selectRouteAt,
    selectRouteWithMods,
    handleClearBrowseSelection,

    // slot bulk edit（スロット高さの一括変更 / 一括平行移動）
    isSavingSlotHeights,
    slotMoveProgress,
    applySlotMoveBulk,

    // map bulk edit session（複数回の移動・回転・高さ変更をまとめて保存）
    editedMap,
    hasPendingMapEdits,
    pendingEditCounts,
    pendingHeights,
    stageRouteMarks,
    stageSlotHeights,
    stageEndpointRotation,
    discardPendingMapEdits,
    applyPendingMapEdits,
    canUndoMapEdit,
    canRedoMapEdit,
    undoMapEdit,
    redoMapEdit,
    beginMapEditBatch,
    endMapEditBatch,

    // computed
    maxScenarios: MAX_SCENARIOS,
    maxPlaybackRoutes: MAX_PLAYBACK_ROUTES,
    enableCargoToggle: ENABLE_CARGO_TOGGLE,
    isMaxScenariosReached,
    cartErrors,
    otherScenarioRouteMapIds,
    currentScenarioRouteMapIds,
    unavailableScenarioIds,
    isEndSlotCargo,
    isCargoStartValid,
    isMoveDisabled,
    isMoveLoadedDisabled,
    isPickupDisabled,
    isPlaceDisabled,
    isAddDisabled,
    isAddWaitDisabled,
    isCreateDisabled,
    isRouteConnectable,
    sortedRoutes,
    distanceInfo,
    previewRoutes,

    // handlers
    handleCreateScenario,
    handleRequestSave,
    handleConfirmSave,
    handleCancelSave,
    handleStartCreate,
    handleStartEdit,
    handleSelectRoute,
    handleConfirmAdd,
    handleSelectWait,
    handlePreviewWaitSecChange,
    handleConfirmAddWait,
    handleCartWaitSecChange,
    handleSelectWaitResume,
    handleConfirmAddWaitResume,
    handleSwapDirection,
    handleRequestDelete,
    handleCancelDelete,
    handleConfirmRouteDelete,
    handleEditorCancel,
    handleConfirmEditorCancel,
    handleDismissEditorCancel,
    handleToggleSelectedRoute,
    handlePreviewActionChange,
    handleRequestListDelete,
    handleConfirmListDelete,
    handleCancelListDelete,
  }
}
